import React from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface ExpiringBatch {
  amount: number;
  expires_at: string;
}

interface ExpiringBonusChipsProps {
  expiringBChip: ExpiringBatch[];
  calculateProgress: (expiryDate: string) => number;
}

const ExpiringBonusChips: React.FC<ExpiringBonusChipsProps> = ({
  expiringBChip,
  calculateProgress,
}) => {
  const getUrgencyColor = (progress: number) => {
    if (progress >= 70) return "#10B981"; // Green
    if (progress >= 30) return "#F59E0B"; // Yellow
    return "#EF4444"; // Red
  };

  const getDaysLeft = (expiryDate: string) => {
    const today = new Date().getTime();
    const expiry = new Date(expiryDate).getTime();
    const daysLeft = Math.max(0, Math.floor((expiry - today) / (1000 * 60 * 60 * 24)));
    return daysLeft;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        mb: 4,
        borderRadius: 5,
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        color: COLORS.text,
        boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        },
        fontFamily: "'Lexend', sans-serif",
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            color: COLORS.text,
            fontSize: { xs: "1rem", md: "1.1rem" },
          }}
        >
          Expiring Bonus Chips
        </Typography>
        <Tooltip title="Bonus chips expire after their validity period">
          <IconButton size="small" sx={{ color: COLORS.muted }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      {expiringBChip.length > 0 ? (
        expiringBChip.map((batch, idx) => {
          const progress = calculateProgress(batch.expires_at);
          const urgencyColor = getUrgencyColor(progress);
          const daysLeft = getDaysLeft(batch.expires_at);

          return (
            <Box
              key={idx}
              mb={2.5}
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 4,
                background: "rgba(255,255,255,0.06)",
                border: `1px solid ${COLORS.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "background 0.3s ease",
                "&:hover": { background: "rgba(255,255,255,0.1)" },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    color: COLORS.text,
                  }}
                >
                  {batch.amount} BChips
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    color: urgencyColor,
                    fontWeight: 600,
                  }}
                >
                  {daysLeft > 0 ? `${daysLeft} days left` : "Expired"}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: COLORS.muted,
                  fontSize: { xs: "0.75rem", md: "0.8rem" },
                }}
              >
                Expires on {new Date(batch.expires_at).toLocaleDateString()}
              </Typography>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: urgencyColor,
                    transition: "width 0.6s ease",
                  },
                }}
              />
            </Box>
          );
        })
      ) : (
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mt: 2,
            color: COLORS.muted,
            fontSize: { xs: "0.85rem", md: "0.9rem" },
          }}
        >
          No expiring bonus chips at the moment.
        </Typography>
      )}
    </Paper>
  );
};

export default ExpiringBonusChips;
